import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';
import { onUpgrade402 } from '../utils/onUpgrade402';

/**
 * Smart Template Engine — restaurant menus (the CONTENT a signage template
 * renders). Company-scoped, bilingual sections + items. Backend
 * RestaurantMenuController (.claude/TEMPLATE_ENGINE_SPEC.md).
 *
 *   GET    /menus        → { menus } (with sections_count)
 *   POST   /menus        create (name, currency, sections[])
 *   GET    /menus/{id}    → { menu } (+ sections + items)
 *   PUT    /menus/{id}    replace name / currency / sections / items
 *   DELETE /menus/{id}
 *
 * Resource-wrapped responses ({ data: { status, ... } }) — unwrap `res?.data ?? res`.
 */

const LIST_KEY = 'signageMenus';
const itemKey = (id) => ['signageMenu', id];

export const useGetMenus = () =>
  useQuery(
    LIST_KEY,
    async () => {
      const res = (await axiosInstance.get('/menus')).data;
      return (res?.data ?? res)?.menus ?? [];
    },
    { staleTime: 15_000 }
  );

export const useGetMenu = (id, enabled = true) =>
  useQuery(
    itemKey(id),
    async () => {
      const res = (await axiosInstance.get(`/menus/${id}`)).data;
      return (res?.data ?? res)?.menu ?? null;
    },
    { enabled: !!id && enabled, staleTime: 5_000 }
  );

export const useCreateMenu = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post('/menus', payload), {
    onSuccess: () => {
      qc.invalidateQueries(LIST_KEY);
      showToast('success', 'Menus', 'Menu created.');
    },
    onError: (err) => showToast('error', 'Menus', err.response?.data?.message),
  });
};

export const useUpdateMenu = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(({ id, ...payload }) => axiosInstance.put(`/menus/${id}`, payload), {
    onSuccess: (_res, vars) => {
      qc.invalidateQueries(LIST_KEY);
      qc.invalidateQueries(itemKey(vars.id));
      showToast('success', 'Menus', 'Menu saved.');
    },
    onError: (err) => showToast('error', 'Menus', err.response?.data?.message),
  });
};

/**
 * AI Menu OCR — POST /menus/import-from-image (multipart `file`: image or PDF).
 * Returns the resource-unwrapped body { status, msg, draft, stats }. The draft
 * is NOT saved — the caller pre-fills the Menu Builder for review. Network/
 * throttle (429) / validation (422) errors throw and toast; an OCR-read failure
 * comes back as HTTP 200 with body.status 422 + msg, which the caller inspects.
 */
export const useImportMenuFromImage = () => {
  const { showToast } = useCustomToast();
  return useMutation(
    async (file) => {
      const form = new FormData();
      form.append('file', file);
      const res = (await axiosInstance.post('/menus/import-from-image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })).data;
      return res?.data ?? res;
    },
    {
      onError: (err) => {
        if (onUpgrade402(err, showToast)) return;
        showToast('error', 'Menus', err.response?.data?.message);
      },
    }
  );
};

export const useDeleteMenu = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.delete(`/menus/${id}`), {
    onSuccess: () => {
      qc.invalidateQueries(LIST_KEY);
      showToast('info', 'Menus', 'Menu deleted.');
    },
    onError: (err) => showToast('error', 'Menus', err.response?.data?.message),
  });
};
